import Phaser from 'phaser';
import type { AudioService } from './AudioService';
import type { MusicService } from './MusicService';
import { AudioSettingsService } from '../../services/audioSettingsService';

/** A high depth so controls sit above every scene's content. */
const CONTROL_DEPTH = 1000;

/** Dimmed alpha for a toggle shown in its "off" state. */
const OFF_ALPHA = 0.4;

/** Speaker / muted-speaker toggle. Persists + applies the mute setting. */
export function addMuteButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  audio: AudioService,
): Phaser.GameObjects.Text {
  const settings = new AudioSettingsService(window.localStorage);
  const glyph = () => (settings.get().muted ? '🔇' : '🔊');

  const btn = scene.add
    .text(x, y, glyph(), { fontSize: '24px' })
    .setOrigin(0.5)
    .setDepth(CONTROL_DEPTH)
    .setInteractive({ useHandCursor: true });

  btn.on('pointerup', () => {
    settings.setMuted(!settings.get().muted);
    audio.applySettings();
    btn.setText(glyph());
  });

  return btn;
}

/**
 * Background-music toggle (🎵). Independent of the master mute — controls only
 * the looping track. Full opacity when on, dimmed when off. Persists the flag
 * and starts/stops the current track via MusicService.
 */
export function addMusicButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  music: MusicService,
): Phaser.GameObjects.Text {
  const settings = new AudioSettingsService(window.localStorage);

  const btn = scene.add
    .text(x, y, '🎵', { fontSize: '24px' })
    .setOrigin(0.5)
    .setDepth(CONTROL_DEPTH)
    .setAlpha(settings.get().musicEnabled ? 1 : OFF_ALPHA)
    .setInteractive({ useHandCursor: true });

  btn.on('pointerup', () => {
    const on = !settings.get().musicEnabled;
    music.setEnabled(on);
    btn.setAlpha(on ? 1 : OFF_ALPHA);
  });

  return btn;
}

/** Tuning for a volume slider instance. Defaults match the full menu slider. */
export interface VolumeSliderOptions {
  /** Track length (px). */
  width?: number;
  /** Show the "🔊 VOLUME" caption above the track. */
  showLabel?: boolean;
  /** Draggable knob radius (px). */
  knobRadius?: number;
}

/**
 * Horizontal master-volume slider (track + fill + draggable knob). Reads/writes
 * the persisted master volume and applies it live. Pass options for a compact,
 * label-less variant. Returns every game object it created so callers that
 * embed it in a transient overlay can tear it down together.
 */
export function addVolumeSlider(
  scene: Phaser.Scene,
  x: number,
  y: number,
  audio: AudioService,
  opts: VolumeSliderOptions = {},
): Phaser.GameObjects.GameObject[] {
  const W = opts.width ?? 160;
  const H = 6;
  const showLabel = opts.showLabel ?? true;
  const knobR = opts.knobRadius ?? 10;
  const left = x - W / 2;
  const settings = new AudioSettingsService(window.localStorage);
  const v0 = settings.get().volume;

  const made: Phaser.GameObjects.GameObject[] = [];

  if (showLabel) {
    made.push(
      scene.add
        .text(x, y - 22, '🔊 VOLUME', { fontSize: '13px', color: '#9aa6ad' })
        .setOrigin(0.5)
        .setDepth(CONTROL_DEPTH),
    );
  }

  const track = scene.add.rectangle(x, y, W, H, 0x555a66).setDepth(CONTROL_DEPTH);
  const fill = scene.add
    .rectangle(left, y, W * v0, H, 0xf5c518)
    .setOrigin(0, 0.5)
    .setDepth(CONTROL_DEPTH);

  const knob = scene.add
    .circle(left + W * v0, y, knobR, 0xffffff)
    .setDepth(CONTROL_DEPTH + 1)
    .setInteractive({ useHandCursor: true });
  scene.input.setDraggable(knob);

  knob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => {
    const clamped = Phaser.Math.Clamp(dragX, left, left + W);
    knob.x = clamped;
    const v = (clamped - left) / W;
    fill.width = W * v;
    settings.setVolume(v);
    audio.applySettings();
  });

  made.push(track, fill, knob);
  return made;
}

/**
 * Audio control popover: a speaker trigger (reflecting the mute state) that
 * toggles a small overlay panel combining the mute button and the master-volume
 * slider. Clicking the trigger again, or anywhere outside the panel, dismisses
 * it. The overlay's objects are created on open and destroyed on close.
 */
export function addAudioControl(
  scene: Phaser.Scene,
  x: number,
  y: number,
  audio: AudioService,
): Phaser.GameObjects.Text {
  const settings = new AudioSettingsService(window.localStorage);
  const triggerGlyph = () => (settings.get().muted ? '🔇' : '🔊');

  const trigger = scene.add
    .text(x, y, triggerGlyph(), { fontSize: '24px' })
    .setOrigin(0.5)
    .setDepth(CONTROL_DEPTH)
    .setInteractive({ useHandCursor: true });

  let popover: Phaser.GameObjects.GameObject[] | null = null;

  const closePopover = (): void => {
    if (!popover) return;
    popover.forEach((o) => o.destroy());
    popover = null;
  };

  const openPopover = (): void => {
    const PW = 200;
    const PH = 128;
    const cx = Phaser.Math.Clamp(x, PW / 2 + 6, scene.scale.width - PW / 2 - 6);
    const cy = y + 20 + PH / 2;

    // Full-screen click-catcher (below the panel) that dismisses on outside click.
    const scrim = scene.add
      .rectangle(
        scene.scale.width / 2,
        scene.scale.height / 2,
        scene.scale.width,
        scene.scale.height,
        0x000000,
        0.01,
      )
      .setDepth(CONTROL_DEPTH - 2)
      .setInteractive();
    scrim.on('pointerup', closePopover);

    // Panel background — interactive so clicks on its dead space don't fall
    // through to the scrim and close it.
    const panel = scene.add
      .rectangle(cx, cy, PW, PH, 0x1a1a33, 0.96)
      .setStrokeStyle(2, 0x6666cc)
      .setDepth(CONTROL_DEPTH - 1)
      .setInteractive();

    const title = scene.add
      .text(cx, cy - PH / 2 + 15, 'AUDIO', {
        fontSize: '13px',
        color: '#aaaaff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(CONTROL_DEPTH + 2);

    const muteLabel = () => (settings.get().muted ? '🔇  Muted' : '🔊  Sound On');
    const muteBg = scene.add
      .rectangle(cx, cy - 14, PW - 24, 30, 0x33334d)
      .setStrokeStyle(1, 0x6666cc)
      .setInteractive({ useHandCursor: true })
      .setDepth(CONTROL_DEPTH + 1);
    const muteTxt = scene.add
      .text(cx, cy - 14, muteLabel(), { fontSize: '14px', color: '#ffffff' })
      .setOrigin(0.5)
      .setDepth(CONTROL_DEPTH + 2);
    muteBg.on('pointerup', () => {
      settings.setMuted(!settings.get().muted);
      audio.applySettings();
      trigger.setText(triggerGlyph());
      muteTxt.setText(muteLabel());
    });

    const cap = scene.add
      .text(cx - (PW - 40) / 2, cy + 20, 'Volume', { fontSize: '11px', color: '#9aa6ad' })
      .setOrigin(0, 0.5)
      .setDepth(CONTROL_DEPTH + 2);
    const sliderObjs = addVolumeSlider(scene, cx, cy + 40, audio, {
      width: PW - 40,
      showLabel: false,
      knobRadius: 9,
    });

    popover = [scrim, panel, title, muteBg, muteTxt, cap, ...sliderObjs];
  };

  trigger.on('pointerup', () => (popover ? closePopover() : openPopover()));
  // Tear down a still-open popover if the scene shuts down (e.g. Abandon).
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, closePopover);

  return trigger;
}
